/**
 * Utility functions for processing chat messages
 */

export interface ParsedMessage {
  content: string;
  sources: string[];
}

/**
 * Parse sources from message content and return clean content with extracted sources
 * 
 * @param rawContent - The raw message content that may contain sources JSON
 * @returns Object with cleaned content and extracted sources array
 */
export function parseMessageSources(rawContent: string): ParsedMessage {
  try {
    // Look for sources JSON at the end of the message
    // More flexible pattern to handle various formats including escaped quotes and backticks
    // Pattern: {"sources": [" `url1` ", " `url2` ", ...]} or similar variations
    const sourcesRegex = /\{["']sources["']:\s*\[([^\]]+)\]\}\s*$/;
    const match = rawContent.match(sourcesRegex);
    
    if (match) {
      // Extract the sources JSON part
      const sourcesJsonStr = match[0];
      
      try {
        // Try to parse the JSON directly first
        const sourcesObj = JSON.parse(sourcesJsonStr);
        if (sourcesObj.sources && Array.isArray(sourcesObj.sources)) {
          // Clean the content by removing the sources JSON
          const cleanContent = rawContent.replace(sourcesRegex, '').trim();
          
          // Clean up the sources URLs (remove backticks, quotes, and extra spaces)
          const cleanSources = sourcesObj.sources.map((source: string) => 
            source.replace(/[`"']/g, '').trim()
          ).filter((source: string) => source.length > 0);
          
          return {
            content: cleanContent,
            sources: cleanSources
          };
        }
      } catch (parseError) {
        // If JSON parsing fails, try to extract URLs manually from the sources array content
        console.warn('JSON parsing failed, trying manual extraction:', parseError);
        
        try {
          // Extract the array content between [ and ]
          const arrayContent = match[1];
          // Split by comma and extract URLs
          const urlMatches = arrayContent.match(/https?:\/\/[^\s,"'`]+/g);
          
          if (urlMatches && urlMatches.length > 0) {
            const cleanContent = rawContent.replace(sourcesRegex, '').trim();
            const cleanSources = urlMatches.map(url => url.trim()).filter(url => url.length > 0);
            
            return {
              content: cleanContent,
              sources: cleanSources
            };
          }
        } catch (manualError) {
          console.warn('Manual extraction also failed:', manualError);
        }
      }
    }
    
    // No sources found or parsing failed, return original content
    return {
      content: rawContent,
      sources: []
    };
  } catch (error) {
    console.error('Error parsing message sources:', error);
    return {
      content: rawContent,
      sources: []
    };
  }
}

/**
 * Extract domain name from URL for display purposes
 * 
 * @param url - The full URL
 * @returns Domain name or the original URL if parsing fails
 */
export function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url;
  }
}

/**
 * Get a favicon URL for a given domain
 * 
 * @param url - The full URL
 * @returns Favicon URL
 */
export function getFaviconUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    return `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=16`;
  } catch {
    return 'https://www.google.com/s2/favicons?domain=example.com&sz=16';
  }
}

/**
 * Strip common markdown syntax for plain-text previews.
 *
 * Used in chat-session sidebar cards where we want a one-line readable
 * snippet of the last bot message. Bot messages are markdown — without
 * stripping, the user sees literal characters like `**bold**`, `### heading`,
 * `[link](url)`, etc. in the card preview, which looks broken.
 *
 * Targeted at the markdown features the bot actually uses: bold/italic,
 * inline code, links, headings, list bullets, blockquotes, and surrounding
 * whitespace. Not a full markdown→text converter — intentionally lightweight
 * because previews are short and the cost of an over-aggressive strip is low.
 */
export function stripMarkdownForPreview(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text
    // Bold / italic / strikethrough — keep inner text, drop markers
    .replace(/\*\*\*([^*]+)\*\*\*/g, '$1')           // ***bold italic***
    .replace(/\*\*([^*]+)\*\*/g, '$1')               // **bold**
    .replace(/__([^_]+)__/g, '$1')                   // __bold__
    .replace(/\*([^*\n]+)\*/g, '$1')                 // *italic*
    .replace(/~~([^~]+)~~/g, '$1')                   // ~~strike~~
    // Inline code — strip the backticks, keep the code text
    .replace(/`([^`]+)`/g, '$1')
    // Images — keep alt text only
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1')
    // Links — keep label only
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Headings, blockquotes, list markers at line start
    .replace(/^#{1,6}\s+/gm, '')                     // # heading
    .replace(/^>\s*/gm, '')                          // > quote
    .replace(/^\s*[-*+]\s+/gm, '')                   // - / * / + bullets
    .replace(/^\s*\d+\.\s+/gm, '')                   // 1. numbered
    // Collapse all whitespace to single spaces so previews stay one-line
    .replace(/\s+/g, ' ')
    .trim();
}